import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import moment from "moment";
import { API, graphqlOperation } from "aws-amplify";
import {
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
} from "@coreui/react";
import Tippy from '@tippyjs/react';
import { Flex, Loader, Text } from "@aws-amplify/ui-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Autorenew, MoreVert, Settings, Delete } from "@mui/icons-material";

import LineChart from "components/charts/LineChart";
import { getDevice, getDeviceDetails, getVariable } from "graphql/queries";
import Empty from "components/utils/Empty";
import { getBranchName, getCompanyName } from "graphql/custom-queries";
import { periods, scheduleTypes } from "config/constants";
import { globalStrings } from "config/translations/es";
import OneValueChart from "components/charts/OneValueChart";
import { formatNumber } from "components/utils/functions";

const DeviceChart = ({ deviceModel, onDelete, onUpdate, draggable }) => {
  const intervalRef = useRef();
  const [loadingChart, setLoadingChart] = useState(false);
  const [deviceState, setDeviceState] = useState({
    loading: false,
    model: null,
  });
  const [companyState, setCompanyState] = useState({
    loading: false,
    model: null,
  });
  const [branchState, setBranchState] = useState({
    loading: false,
    model: null,
  });
  const [variableState, setVariableState] = useState({
    loading: false,
    model: null,
  });
  const [labels, setLabels] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: deviceModel.index });

  const styleSortableCard = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStartDateByPeriod = (value, dateNow) => {
    let startDate = moment(dateNow).startOf("day");

    if (value === "lastHour") startDate = moment(dateNow).subtract(1, "hour");
    if (value === "today") startDate = moment(dateNow).startOf("day");
    if (value === "yesterday")
      startDate = moment(dateNow).subtract(1, "day").startOf("day");
    if (value === "thisWeek")
      startDate = moment(dateNow).startOf("week").startOf("day");
    if (value === "last7Days")
      startDate = moment(dateNow).subtract(7, "days").startOf("day");
    if (value === "last14Days")
      startDate = moment(dateNow).subtract(14, "days").startOf("day");
    if (value === "last30Days")
      startDate = moment(dateNow).subtract(30, "days").startOf("day");
    if (value === "last60Days")
      startDate = moment(dateNow).subtract(60, "days").startOf("day");
    if (value === "thisYear")
      startDate = moment(dateNow).startOf("year").startOf("day");

    return startDate;
  };

  async function fetchCompanyName(id) {
    setCompanyState({
      loading: true,
    });
    const companyData = await API.graphql(
      graphqlOperation(getCompanyName, {
        id,
      })
    );
    const companyItem = companyData.data.getCompany;
    setCompanyState({
      loading: false,
      model: companyItem,
    });
  }

  async function fetchBranchName(id) {
    setBranchState({
      loading: true,
    });
    const branchData = await API.graphql(
      graphqlOperation(getBranchName, {
        id,
      })
    );
    const branchItem = branchData.data.getBranch;
    setBranchState({
      loading: false,
      model: branchItem,
    });
  }

  async function fetchVariable(id) {
    setVariableState({
      loading: true,
    });
    const variableData = await API.graphql(
      graphqlOperation(getVariable, {
        id,
      })
    );
    const variableItem = variableData.data.getVariable;
    setVariableState({
      loading: false,
      model: variableItem,
    });
    return variableItem;
  }

  async function fetchDevice() {
    const { deviceId } = deviceModel;

    setDeviceState({
      loading: true,
    });
    const deviceData = await API.graphql(
      graphqlOperation(getDevice, {
        id: deviceId,
      })
    );
    const deviceItem = deviceData.data.getDevice;
    setDeviceState({
      loading: false,
      model: deviceItem,
    });
    if (deviceItem) {
      fetchCompanyName(deviceItem.companyID);
    }
    return deviceItem;
  }

  async function fetchDeviceDetails(
    variableObj,
    startDate,
    endDate,
    groupBy,
    lastValue
  ) {
    try {
      const { deviceId, graphBy, companyId, branchId } = deviceModel;
      setLoadingChart(true);

      const deviceDetails = await API.graphql(
        graphqlOperation(getDeviceDetails, {
          deviceId,
          variableName: variableObj.shortName,
          variableDerived: variableObj.derived,
          startDate,
          endDate,
          groupBy,
          isAmPm: deviceModel?.scheduleType === scheduleTypes[0],
          graphBy,
          companyId,
          branchId,
          lastValue,
        })
      );
      const deviceDetailsItems = deviceDetails.data.getDeviceDetails;
      if (deviceDetailsItems.data?.length > 0) {
        if (lastValue) {
          setDatasets([
            {
              lastValue: true,
              data:
                variableObj.type === "onOff" ? (
                  deviceDetailsItems.data[0] === 0 ? (
                    <Text variation="error">Off</Text>
                  ) : (
                    <Text variation="success">On</Text>
                  )
                ) : (
                  formatNumber(deviceDetailsItems.data[0], 2)
                ),
              unit: variableObj.type !== "onOff" && variableObj.unit,
              date: moment(deviceDetailsItems.labels[0]).format(
                deviceModel?.scheduleType === scheduleTypes[0]
                  ? "YYYY/MM/DD hh:mm:ss a"
                  : "YYYY/MM/DD HH:mm:ss"
              ),
            },
          ]);
        } else {
          setLabels(deviceDetailsItems.labels);
          setDatasets([
            {
              onOff:
                variableObj.type === "onOff" &&
                deviceDetailsItems.xAxisLabel === "minutes",
              label: variableObj.name,
              data: deviceDetailsItems.data,
              borderColor: deviceModel.chartColor,
              backgroundColor: deviceModel.chartColor,
              borderWidth: 1,
              pointRadius: 2,
              unit: variableObj.unit,
              xAxisLabel:
                deviceDetailsItems.xAxisLabel &&
                globalStrings[deviceDetailsItems.xAxisLabel],
            },
          ]);
        }
      } else {
        setLabels([]);
        setDatasets([]);
      }
      setLoadingChart(false);
    } catch (err) {
      setLoadingChart(false);
      toast.error(strings.responses.errorFetchData);
    }
  }

  const initData = async () => {
    const dateNow = moment();
    const startDate =
      deviceModel.period === periods[periods.length - 1]
        ? moment(deviceModel.dateFrom)
        : getStartDateByPeriod(deviceModel.period, dateNow);
    const endDate =
      deviceModel.period === periods[periods.length - 1]
        ? moment(deviceModel.dateTo)
        : dateNow;
    const groupBy = deviceModel.groupBy;

    const deviceObj =
      deviceModel.graphBy !== "company" &&
      deviceModel.graphBy !== "branch" &&
      (await fetchDevice());

    if (deviceModel.graphBy === "company")
      fetchCompanyName(deviceModel.companyId);
    else if (deviceModel.graphBy === "branch") {
      fetchCompanyName(deviceModel.companyId);
      fetchBranchName(deviceModel.branchId);
    }

    const variableObj = await fetchVariable(deviceModel.variableId);
    fetchDeviceDetails(
      variableObj,
      startDate.toISOString(),
      endDate.toISOString(),
      groupBy,
      deviceModel.period === "lastValue"
    );

    if (intervalRef.current) clearInterval(intervalRef.current);

    if (!!deviceModel.autoRefresh) {
      const autoRefreshTime =
        deviceObj?.autoRefreshTime > 0
          ? deviceObj?.autoRefreshTime * 1000
          : 35000;
      intervalRef.current = setInterval(() => {
        const dateNowInterval = moment();
        const startDateInterval =
          deviceModel.period === periods[periods.length - 1]
            ? moment(deviceModel.dateFrom)
            : getStartDateByPeriod(deviceModel.period, dateNowInterval);
        const endDateInterval =
          deviceModel.period === periods[periods.length - 1]
            ? moment(deviceModel.dateTo)
            : dateNowInterval;
        fetchDeviceDetails(
          variableObj,
          startDateInterval.toISOString(),
          endDateInterval.toISOString(),
          groupBy,
          deviceModel.period === "lastValue"
        );
      }, autoRefreshTime);
    }
  };

  useEffect(() => {
    initData();
    return () => {
      clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deviceModel?.deviceId,
    deviceModel?.variableId,
    deviceModel?.period,
    deviceModel?.dateFrom,
    deviceModel?.dateTo,
    deviceModel?.scheduleType,
    deviceModel?.groupBy,
    deviceModel?.chartColor,
    deviceModel?.autoRefresh,
    deviceModel?.graphBy,
    deviceModel?.companyId,
    deviceModel?.branchId,
  ]);

  return (
    <>
      <CCard style={styleSortableCard} ref={setNodeRef}>
        <CCardHeader
          style={{
            width: `${deviceModel.width}px`,
            padding: "14px",
          }}
        >
          <Flex justifyContent="space-between">
            <div style={{ overflow: "hidden" }}>
              {variableState.loading ? (
                <Loader />
              ) : (
                <Tippy content={variableState.model?.name ?? ""}>
                  <h5
                    className="text-muted"
                    style={styles.headTitle}
                    {...(draggable && { ...attributes, ...listeners })}
                  >
                    {variableState.model?.name ?? ""}
                  </h5>
                </Tippy>
              )}
              <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                {companyState.loading ? (
                  <Loader />
                ) : (
                  <b>{companyState.model?.name ?? ""}</b>
                )}
                <br />
                {deviceModel.graphBy === "branch" ? (
                  <>
                    {branchState.loading ? (
                      <Loader />
                    ) : (
                      <b>{branchState.model?.name ?? ""}</b>
                    )}
                  </>
                ) : (
                  <>
                    {deviceState.loading ? (
                      <Loader />
                    ) : (
                      <b>{deviceState.model?.name ?? ""}</b>
                    )}
                  </>
                )}
              </p>
            </div>
            <Flex direction="column" rowGap={6}>
              <CDropdown variant="dropdown" style={{ textAlign: "right" }}>
                <CDropdownToggle
                  placement="bottom-end"
                  className="py-0 pe-0 border-0"
                  caret={false}
                >
                  <MoreVert color="action"></MoreVert>
                </CDropdownToggle>
                <CDropdownMenu className="pt-0" placement="bottom-end">
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-2"
                    onClick={() => initData()}
                  >
                    <Autorenew
                      fontSize="small"
                      color="action"
                      className="me-1"
                    />{" "}
                    {strings.refresh}
                  </CDropdownItem>
                  {onUpdate && (
                    <CDropdownItem
                      role="button"
                      className="d-inline-flex align-items-center"
                      onClick={() => onUpdate()}
                    >
                      <Settings
                        fontSize="small"
                        color="action"
                        className="me-1"
                      />{" "}
                      {strings.config}
                    </CDropdownItem>
                  )}
                  {onDelete && (
                    <CDropdownItem
                      role="button"
                      className="d-inline-flex align-items-center"
                      onClick={() => onDelete()}
                    >
                      <Delete
                        fontSize="small"
                        color="action"
                        className="me-1"
                      />{" "}
                      {strings.delete}
                    </CDropdownItem>
                  )}
                </CDropdownMenu>
              </CDropdown>
            </Flex>
          </Flex>
        </CCardHeader>
        <CCardBody
          style={{
            ...styles.cardBody,
            width: `${deviceModel.width}px`,
            height: `${deviceModel.height}px`,
          }}
        >
          {loadingChart ? (
            <Loader />
          ) : datasets?.length > 0 ? (
            !!datasets[0].lastValue ? (
              <OneValueChart
                value={datasets[0].data}
                legend={datasets[0].unit}
                footer={datasets[0].date}
              />
            ) : (
              <LineChart labels={labels} datasets={datasets} />
            )
          ) : (
            <Empty />
          )}
        </CCardBody>
        <CCardFooter
          style={{ display: "inline-flex", justifyContent: "space-between" }}
        >
          <span style={styles.textDate} className="text-muted">
            {globalStrings.dateItems[deviceModel.period]}
          </span>
          <span style={styles.textDate} className="text-muted">
            {deviceState.model?.timezone ?? ""}
          </span>
        </CCardFooter>
      </CCard>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    successDeviceOn: "El dispositivo fue encendido con éxito",
    successDeviceOff: "El dispositivo fue apagado con éxito",
    responses: {
      errorFetchData: "Error al consultar datos",
      errorFetchActions: "Error al consultar acciones del dispositivo",
      errorSendAction: "Error al realizar la acción",
    },
    selectDateRange: "Seleccionar Rango de Fecha",
    move: "Mover",
    refresh: "Refrescar",
    config: "Configuración de Tarjeta",
    delete: "Eliminar",
  },
});

const styles = {
  iconActionMenu: {
    fontSize: "26px",
  },
  iconAction: {
    fontSize: "24px",
  },
  dropdown: {
    float: "right",
  },
  dropdownToggle: {
    border: "none",
    padding: "0px 5px",
  },
  dropdownItem: {
    cursor: "pointer",
  },
  dropdownMenu: {
    maxHeight: "240px",
  },
  textDate: {
    fontSize: "14px",
  },
  action: {
    // alignSelf: "center",
    padding: "0px 1px",
  },
  cardBody: {
    minHeight: "100px",
    minWidth: "250px",
  },
  headTitle: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

export default DeviceChart;
